/**
 * GPU-resource release for whole scene subtrees (audit §2.6 #1).
 *
 * Why this exists as its own module rather than another local helper: three keeps the resources
 * of an `Object3D` alive long after the last JS reference to it is gone. `WebGLBindingStates`
 * retains `bindingStates[geometry.id].attributes`, `WebGLGeometries` retains the uploaded
 * attribute buffers, `WebGLPrograms` retains the compiled program, `WebGLTextures` retains the
 * uploaded texel data — and every one of those caches drops its entry only when it receives the
 * `'dispose'` event that `BufferGeometry.dispose()` / `Material.dispose()` / `Texture.dispose()`
 * / `Skeleton.dispose()` dispatch. Detaching a root from the scene graph frees nothing. That is
 * exactly why the leak in §2.6 #1 survived: the code that swapped models removed the old root
 * and dropped the reference, which reads like a release and is not one (~16 MB retained per swap
 * on a 200 k-vertex mesh).
 *
 * Before 2026-08-22 the only correct disposal in the editor was `disposeCompanion` in
 * `previewController.ts`. This module is that function's body, lifted so the companion path and
 * the main-model path cannot drift apart again.
 *
 * **Everything here is duck-typed and imports three for TYPES ONLY.** `src/editor/session.ts` is
 * the state layer and its header promises not to pull runtime modules into the bundle through
 * the state layer; a helper with no runtime imports keeps that promise. It also means these
 * functions work under vitest's `node` environment with no WebGL and no canvas, which is the
 * only way the disposal tests can exist at all.
 */

import type { BufferGeometry, Material, Object3D, Skeleton, Texture } from 'three';

/**
 * The four kinds of long-lived GPU resource the editor's scene objects carry.
 *
 * Sets, not arrays: a subtree routinely reaches the same material from several meshes, and one
 * texture from several materials. Disposing twice is harmless in three (the caches remove their
 * listener on the first `'dispose'`), but the counts are what the disposal tests assert on, so
 * de-duplicating here keeps "released once" observable.
 */
export interface SceneResources {
  geometries: Set<BufferGeometry>;
  materials: Set<Material>;
  textures: Set<Texture>;
  skeletons: Set<Skeleton>;
}

/** Structural view of the parts of `Mesh`/`SkinnedMesh` that matter here. */
interface Renderable {
  isMesh?: boolean;
  geometry?: BufferGeometry;
  material?: Material | Material[];
  skeleton?: Skeleton;
}

export function createSceneResources(): SceneResources {
  return {
    geometries: new Set<BufferGeometry>(),
    materials: new Set<Material>(),
    textures: new Set<Texture>(),
    skeletons: new Set<Skeleton>(),
  };
}

/** Is this arbitrary material property a texture we are expected to free? */
function asTexture(value: unknown): Texture | null {
  const candidate = value as { isTexture?: boolean; dispose?: unknown } | null | undefined;
  if (!candidate || candidate.isTexture !== true) return null;
  if (typeof candidate.dispose !== 'function') return null;
  return candidate as unknown as Texture;
}

function collectMaterial(material: Material | null | undefined, into: SceneResources): void {
  if (!material || into.materials.has(material)) return;
  into.materials.add(material);
  // Textures hang off a material as plain properties (`map`, `normalMap`, `emissiveMap`, …) and
  // the set differs per material class, so enumerate rather than list. GLTFLoader is the only
  // thing that puts textures on an imported model's materials, and it creates them per parse.
  for (const value of Object.values(material as unknown as Record<string, unknown>)) {
    const texture = asTexture(value);
    if (texture) into.textures.add(texture);
  }
}

/**
 * Walk a subtree and record every GPU resource hanging off it.
 *
 * Collection is separate from disposal on purpose: the caller needs to compare two subtrees
 * (`sharesResource`) before deciding that the outgoing one is really finished. A collect-and-free
 * one-shot cannot express "this new root is the same model re-skinned, keep everything".
 */
export function collectSceneResources(
  root: Object3D,
  into: SceneResources = createSceneResources(),
): SceneResources {
  root.traverse((object: Object3D) => {
    const node = object as unknown as Renderable;
    // Checked before the `isMesh` gate, matching the companion path: the skeleton's bone texture
    // is a GPU resource whether or not the node that carries it is a renderable mesh.
    if (node.skeleton) into.skeletons.add(node.skeleton);
    if (node.isMesh !== true) return;
    if (node.geometry) into.geometries.add(node.geometry);
    const materials = Array.isArray(node.material) ? node.material : [node.material];
    for (const material of materials) collectMaterial(material, into);
  });
  return into;
}

/** Do these two sets have any resource object in common? */
export function sharesResource(a: SceneResources, b: SceneResources): boolean {
  for (const geometry of a.geometries) if (b.geometries.has(geometry)) return true;
  for (const material of a.materials) if (b.materials.has(material)) return true;
  for (const texture of a.textures) if (b.textures.has(texture)) return true;
  for (const skeleton of a.skeletons) if (b.skeletons.has(skeleton)) return true;
  return false;
}

/**
 * Free everything in the set.
 *
 * Order is skeletons → geometries → materials → textures only for readability; three's caches are
 * independent, so nothing here depends on it.
 */
export function disposeSceneResources(resources: SceneResources): void {
  for (const skeleton of resources.skeletons) skeleton.dispose();
  for (const geometry of resources.geometries) geometry.dispose();
  for (const material of resources.materials) material.dispose();
  for (const texture of resources.textures) texture.dispose();
}

/**
 * Collect and free a subtree in one step.
 *
 * Safe only when nothing outside the subtree still uses what is under it. That holds for a
 * companion (its own `importGlbFromArrayBuffer` call; GLTFLoader parses fresh geometries,
 * materials and textures per parse, so nothing under a companion root is shared with the open
 * model or another companion) and for a model that is being abandoned. It does NOT hold for the
 * re-skin swap, where the skinned clone shares its material object with the source mesh that
 * stays alive for the next generation (`src/rig/skeletonBuilder.ts`, `createSkinnedMesh` passes
 * `mesh.material` straight through) — that path goes through `session.setModel`, which keeps the
 * import's resources owned instead of freeing them.
 */
export function disposeSubtree(root: Object3D): void {
  disposeSceneResources(collectSceneResources(root));
}
